import { Component, OnInit } from '@angular/core';
import { TemplateService } from '../../core/services/template/template.service';
import { UserService } from '../../core/services/user/user.service';
import { ActivatedRoute, Data, NavigationEnd, Router } from '@angular/router';
import { SeoService } from 'src/app/shared/services';
import { filter, map, mergeMap, tap } from 'rxjs/operators';
import { LazyLoadService, LOADING_STRATEGY } from '@abp/ng.core';
import { concat } from 'rxjs';
import { LOCAL_STORAGE_KEYS } from 'src/app/shared';
import { AchievementsWelcomeService } from 'src/app/shared/components/badges/achievements-welcome/achievements-welcome.service';
import { AchievementsCelebrationService } from 'src/app/shared/components/badges/achievements-celebration/achievements-celebration.service';

@Component({
  selector: 'app-root',
  template: `
    <div [innerHTML]="staticContent" *ngIf="!isLoaded" style="display: none;" id="pre-load-content"></div>
    <abp-loader-bar></abp-loader-bar>
    <abp-dynamic-layout></abp-dynamic-layout>
  `,
})
export class AppComponent implements OnInit {
  staticContent: string = '';
  isLoaded: boolean = false;
  private previousUrl: string = undefined;
  private currentUrl: string = undefined;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private seoService: SeoService,
    templateService: TemplateService,
    private userService: UserService,
    private lazyLoadService: LazyLoadService,
    private achievementsWelcomeService: AchievementsWelcomeService,
    private achievementsCelebrationService: AchievementsCelebrationService
  ) {
    templateService.replaceComponent();
    this._setHtmlContentForPagesBeforeRealPageLoadForCrawlers();
    this._setPreviousRoute();
  }

  ngOnInit(): void {
    this.userService.sendHeartBeat();
  }

  private _setHtmlContentForPagesBeforeRealPageLoadForCrawlers(): void {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const url = event.urlAfterRedirects.split('?')[0]; // Remove query params
        this.staticContent = this.seoService.setSeo(url);
        setTimeout(() => this.isLoaded = true, 0); // Simulate async load
      }
    });
  }

  /**
   *  This method sets up SEO tags based on the current route.
   *  It listens for navigation events, retrieves the activated route,  
   *  Due to not having a server-side rendering, this method is not used at the moment.
   */
  private setSeoValuesForPages(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter(route => route.outlet === 'primary'),
        mergeMap(route => route.data),
        tap(({ title, description }: Data) => {
          this._setupSeoTagsData(title, description);
          this.addNgxDataTableStyles(this.router.url);
        })
      )
      .subscribe();

  }

  private _setupSeoTagsData(title: string, description: string): void {
    if (title && description) {
      this.seoService.updateTitle(title);
      this.seoService.updateDescription(description);
    } else {
      this.seoService.updateTitle('algoleague | competitive programming platform in Turkey');
      this.seoService.updateDescription(
        'algoleague is a competitive programming platform where algorithm enthusiasts of all levels practice their algorithmic thinking skills, and compete in contests both in teams and individually'
      );
    }
    this.seoService.setCanonicalTag(window.location.href);
  }

  private addNgxDataTableStyles(url: string): void {
    const administrationURLs = ['/identity/roles', '/identity/users', '/tenant-management/tenants'];
    const ngxStyles = [
      '../../../assets/css/ngx-datatable/ngx-datatable-index.css',
      '../../../assets/css/ngx-datatable/ngx-datatable-icons.css',
      '../../../assets/css/ngx-datatable/ngx-datatable-material.css',
      '../../../assets/css/ngx-datatable/ngx-datatable-custom.css',
    ];
    const loadedStyles = Array.from(this.lazyLoadService.loaded.keys());
    if (administrationURLs.some(x => x === url) && !loadedStyles.some(x => ngxStyles.includes(x))) {
      const ngxDataTableStyles$ = ngxStyles.map(style =>
        this.lazyLoadService.load(LOADING_STRATEGY.AppendAnonymousStyleToHead(style))
      );
      concat(...ngxDataTableStyles$).subscribe();
    }
  }

  private _setPreviousRoute(): void {
    this.currentUrl = this.router.url;
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.previousUrl = this.currentUrl;
        localStorage.setItem(LOCAL_STORAGE_KEYS.PREVIOUS_URL, this.previousUrl);
        this.currentUrl = event.url;
        this.achievementsWelcomeService.checkOnce();
        this.achievementsCelebrationService.check();
      };
    });
  }
}
